import { Box, Button, Dialog, DialogTitle, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import SelectArrow from '../styles/icons/SelectArrow';
import useAutomations from '../hooks/automationhooks/useAutomations';
import { useParams } from 'react-router-dom';

export default function TemplateCreation() {
    const { handleCreateSmsTemplate, handleCreateWhatsappTemplate, handleGetSmsTemplates, handleGetWhatsappTemplates } = useAutomations();
    const { TemplateType, brandId } = useParams();
    const [text, setText] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [nameText, setNameText] = useState("");
    const [nameTouched, setNameTouched] = useState(false);
    const [contentTouched, setContentTouched] = useState(false);
    const [selectedVariables, setSelectedVariables] = useState([]);
    const [validationError, setValidationError] = useState("");

    // Live validation on content change
    useEffect(() => {
        if (TemplateType === "Whatsapp" && contentTouched) {
            validateWhatsappTemplate(); // This will set validation error text
        }
    }, [text, selectedVariables]);

    const handleNameChange = (e) => {
        setNameText(e.target.value);
    };

    const handleTextChange = (e) => {
        setText(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const templateData = Object.fromEntries(formData.entries());

        if (TemplateType === "Whatsapp") {
            if (!validateWhatsappTemplate()) {
                return;
            }
            templateData.templateVariables = selectedVariables;
            await handleCreateWhatsappTemplate(brandId, templateData);
            setDialogOpen(true)
            handleGetWhatsappTemplates(brandId)
        } else {
            await handleCreateSmsTemplate(brandId, templateData);
            setDialogOpen(true)
            handleGetSmsTemplates(brandId)
        }
    };

    // Pure checker (safe to use in render)
    const isValidWhatsappTemplateStructure = () => {
        const tokenMatches = text.match(/\[#(\d+)#\]/g);
        if (!tokenMatches) return false;

        const numbers = tokenMatches.map(t => parseInt(t.match(/\[#(\d+)#\]/)[1]));
        const isSequential = numbers.every((n, i) => n === i + 1);
        const allSelected = selectedVariables.length === numbers.length && selectedVariables.every(v => v);

        return isSequential && allSelected;
    };

    // Form submission + live error text
    const validateWhatsappTemplate = () => {
        const tokenMatches = text.match(/\[#(\d+)#\]/g);

        // If no tokens at all, it's still valid
        if (!tokenMatches) {
            setValidationError("");
            return true;
        }

        const numbers = tokenMatches.map(t => parseInt(t.match(/\[#(\d+)#\]/)[1]));
        const isSequential = numbers.every((n, i) => n === i + 1);
        const allSelected = selectedVariables.length === numbers.length && selectedVariables.every(v => v);

        if (!isSequential) {
            setValidationError("Variables must be numbered in order: [#1#], [#2#], etc.");
            return false;
        }

        if (!allSelected) {
            setValidationError("Please select a value for each variable.");
            return false;
        }

        setValidationError("");
        return true;
    };

    const renderSelects = (text) => {
        const matches = text.match(/\[#(.*?)#\]/g);
        if (!matches) return null;

        return matches.map((part, i) => {
            const label = part.replace(/\[#(.*?)#\]/g, '$1');
            return (
                <div key={i}>
                    <span>{label}: </span>
                    {TemplateType === "Whatsapp" && (
                        <Select
                            size="small"
                            IconComponent={SelectArrow}
                            value={selectedVariables[i] || ""}
                            onChange={(e) => {
                                const newSelections = [...selectedVariables];
                                newSelections[i] = e.target.value;
                                setSelectedVariables(newSelections);
                            }}
                            sx={{
                                margin: "0 5px",
                                fontSize: "0.875rem",
                                mt: 0.5,
                                pr: 0.5,
                                borderRadius: 4,
                                minWidth: 100
                            }}
                        >
                            <MenuItem value="" disabled>Select option</MenuItem>
                            <MenuItem value="FirstName">First Name</MenuItem>
                            <MenuItem value="LastName">Last Name</MenuItem>
                            <MenuItem value="FullName">Full Name</MenuItem>
                            <MenuItem value="Id">Id</MenuItem>
                            <MenuItem value="Phone">Phone</MenuItem>
                        </Select>
                    )}
                </div>
            );
        });
    };

    return (
        <>
            <Box
                component='form'
                onSubmit={handleSubmit}
                sx={{
                    height: '100%',
                    minHeight: '100%'
                }}
            >
                <Box>
                    <Box sx={{ mb: 2, mt: 2, ml: 2 }}>
                        <Typography color='companyBlue' variant="h6" gutterBottom>
                            {TemplateType} Template Creation
                        </Typography>
                    </Box>
                </Box>
                {/* Title section with dark background (default) */}

                {/* Form content section with light grey background */}
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        p: 3,
                        borderRadius: 1,
                        mb: 2,
                        height: '100%'
                    }}
                >
                    <InputLabel>Template Name</InputLabel>
                    <TextField
                        name='templateName'
                        error={!nameText && nameTouched}
                        onClick={() => setNameTouched(true)}
                        onInput={handleNameChange}
                        sx={{ minWidth: 500 }}
                    />
                    <br /><br /><br />
                    <div>Instructions: wrap all variables in [# #].</div>
                    {TemplateType === "Whatsapp" && (
                        <div>
                            Message content with placeholders for parameters, parameters are written in the following form [#1#] [#2#] and so on. <br />
                            The template must comply with the following regulations:
                            <ol>
                                <li>Variables cannot be entered in a template one after the other. Variables must be separated by one or more words.</li>
                                <li>Do not use special characters such as &amp;, +, &gt;, &lt; in the template text.</li>
                                <li>Do not place a variable or a link at the beginning or at the end of the text.</li>
                                <li>It is not possible to enter several empty lines in a row in order to create a space between paragraphs in the template.</li>
                            </ol>
                        </div>
                    )}
                    <InputLabel>Template Content</InputLabel>
                    <TextField
                        name='templateContent'
                        error={!text && contentTouched}
                        onClick={() => setContentTouched(true)}
                        onInput={handleTextChange}
                        multiline
                        rows={7}
                        size='medium'
                        sx={{ minWidth: 500 }}
                    />
                    {TemplateType === "Whatsapp" && validationError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {validationError}
                        </Typography>
                    )}

                    {TemplateType === "SMS" && (
                        <>
                            <InputLabel>Description</InputLabel>
                            <TextField
                                name='templateDescription'
                                multiline
                                rows={7}
                                size='medium'
                                sx={{ minWidth: 500 }}
                            />
                        </>
                    )}

                    <div>{renderSelects(text)}</div>
                    {/* Button section */}
                    <Button
                        color='companyBlue'
                        aria-label='submit'
                        type='submit'
                        variant='contained'
                        disabled={
                            !nameText || !text ||
                            (TemplateType === "Whatsapp" && !isValidWhatsappTemplateStructure())
                        }
                        sx={{
                            mt: 2,
                            borderRadius: '50px',
                            height: '40px',
                            textTransform: 'none',
                        }}
                    >
                        <Typography color='text.primary'>Submit</Typography>
                    </Button>
                </Box>
            </Box>
            <Dialog open={dialogOpen}>
                <DialogTitle>
                        Template created successfully. You may close the window.
                </DialogTitle>
            </Dialog>
        </>
    );
}